import { useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { availableCustomAttributesByType } from 'lib/customAttributes';
import { Button } from 'components/common';
import { Box } from 'components/common/box';
import { ProductCustomAttributes } from 'components/common/customAttributes';
import { Form, FormSection } from 'components/common/form';
import { UnsavedFormChanges } from 'components/common/notifications';
import { AchievementSettingsDetails } from '.';

import type { FormApi } from 'informed';

const AchievementSettings = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { achievement },
    serverConfig
  } = useReduxState();

  const formApi = useRef<FormApi>(undefined);

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      const { categories, ...achievementSettings } = values;

      const data = {
        ...achievementSettings,
        categories: categories || []
      };

      setLoading(true);

      const response = await api.put(
        `/admin/achievements/${achievement.id}`,
        data
      );
      if (!response.data || !response.data.success) throw new Error('error');

      const achievementsResponse = await api.get(
        `/admin/achievements?id=${achievement.id}`
      );

      setLoading(false);
      dispatch.updatePageState({
        achievement: achievementsResponse.data.achievements[0],
        hasUnsavedChanges: false
      });
      dispatch.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      });
    } catch (error) {
      await dispatch.handleErrorDispatch(error);
    }
  };

  const validateTitle = async (value: string) => {
    if (!value || !value.trim()) return messages.commonTitleEmpty;

    try {
      await api.head(
        `/admin/achievements?title=${value}&id=ne|${achievement.id}`
      );

      return messages.commonTitleInUse;
    } catch (err) {
      return undefined;
    }
  };

  const initialValues = {
    allowAnyoneLinkingToCalendarEvents:
      achievement.allowAnyoneLinkingToCalendarEvents,
    categories: achievement.categories || [],
    customAttributes: achievement.customAttributes,
    description: achievement.description,
    enableAutomaticDeletionOfCompletionItems:
      achievement.enableAutomaticDeletionOfCompletionItems,
    enableAutomaticDeletionOfCompletionItemsMonths:
      achievement.enableAutomaticDeletionOfCompletionItemsMonths,
    externalProvider: achievement.externalProvider,
    externalUrl: achievement.externalUrl,
    title: achievement.title
  };

  return (
    <Box padding={0} title={messages.commonSettings}>
      <Form
        allowEmptyStrings={true}
        getApi={(form) => (formApi.current = form)}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ formState }) => (
          <>
            <UnsavedFormChanges
              initialValues={initialValues}
              styles={{ margin: '15px 15px' }}
            />

            <div>
              <AchievementSettingsDetails validateTitle={validateTitle} />

              {availableCustomAttributesByType({
                serverConfig,
                type: achievement.type
              })?.length ? (
                <FormSection
                  maxWidth="100%"
                  title={messages.settingsAdditionalSettings}
                >
                  <div className="custom-attribute-settings">
                    <ProductCustomAttributes
                      attributes={achievement.customAttributes}
                      references={{
                        expiration: achievement.monthsBeforeExpiration,
                        workload: achievement.workload
                      }}
                      type={achievement.type}
                    />
                  </div>
                </FormSection>
              ) : null}

              <FormSection>
                <Button
                  category="success"
                  disabled={formState.invalid}
                  loading={loading}
                  text={messages.commonSaveChanges}
                  type="submit"
                />
              </FormSection>
            </div>
          </>
        )}
      </Form>
    </Box>
  );
};

export default AchievementSettings;
